import React, { useRef, useState } from 'react';
import { Card, Container, Divider, Stack } from '@fiverr-private/layout_components';
import { Image } from '@fiverr-private/media';
import { Heading, Text } from '@fiverr-private/typography';
import { getCurrencySymbol } from '@fiverr-private/localization';
import { I18n } from '@fiverr-private/i18n-react';
import { CheckIcon } from '@fiverr-private/visuals';
import { Button } from '@fiverr-private/button';
import { useModalContext } from '@fiverr-private/overlay';
import { Checkbox } from '@fiverr-private/forms';
import { getContext } from '@fiverr-private/fiverr_context';
import { useEnteredView } from '@fiverr-private/hooks';
import { SupportLink } from '../../../DocumentLinks';
import { formatPrice } from '../../../../utils/currency';
import { LegalTermsDisclaimer } from '../../../LegalTermsDisclaimer/LegalTermsDisclaimer';
import { updateDoNotShowAgainModal } from '../../../../api';
import { reportEventsApi } from '../../../../biEvents';
import { SendModalStepProps } from './types';

const LEGAL_DOCUMENTS_DISCLAIMER_IMAGE_URL =
    'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/53b4c1698cf5e9b8b7e7c65f77a8a130-1736079881526/legal_documents_disclaimer_image.png';
const TRANSLATION_PREFIX = 'legal_documents_package.send_modal.essential_package_disclaimer';

const BENEFITS_KEYS = ['manage_documents', 'documents_sign_method', 'monitor_status'];

export const BasicPlanDisclaimerStep = ({ configurations, sellerName, onSubmit, biData }: SendModalStepProps) => {
    const [skipNextTime, setSkipNextTime] = useState(false);

    const { price, currency } = configurations.legalDocumentsPackageData;
    const { close } = useModalContext();

    const { userId } = getContext();

    const handleSubmit = async () => {
        if (skipNextTime) {
            localStorage.setItem(`skipNextTime_${userId}`, 'true');
            setSkipNextTime(true);
            await updateDoNotShowAgainModal();
        }
        onSubmit();
    };

    const observableRef = useRef<HTMLDivElement>(null);

    useEnteredView(observableRef, {
        threshold: 1,
        onEntered: () => {
            reportEventsApi.viewedModalRequestLegalDocs({ pageName: biData.pageName });
        },
    });

    return (
        <Stack ref={observableRef} direction="column" overflow="auto">
            <Stack direction="column" maxWidth="536px" overflow="auto" height="100%">
                <Image alt="legal documents" src={LEGAL_DOCUMENTS_DISCLAIMER_IMAGE_URL} />
                <Heading size={{ default: 'h_sm', sm: 'h_md' }} paddingTop="5">
                    <I18n k={`${TRANSLATION_PREFIX}.title`} />
                </Heading>
                <Text size="b_md" paddingBottom="5">
                    <I18n
                        k={`${TRANSLATION_PREFIX}.description`}
                        templates={{
                            link: (text) => <SupportLink text={text as string} />,
                        }}
                    />
                </Text>

                <Card borderRadius="md">
                    <Text paddingX="6" paddingY="4" fontWeight="bold" size="b_md">
                        <I18n k={`${TRANSLATION_PREFIX}.service_title`} />
                    </Text>
                    <Divider />
                    <Stack direction="column" paddingX="5" paddingY="5" gap="4">
                        <Stack direction="column" gap="2">
                            {BENEFITS_KEYS.map((key) => (
                                <ListEntry translationKey={key} key={key} />
                            ))}
                        </Stack>
                        <Divider />
                        <Stack gap={{ default: '6', sm: '10' }}>
                            <Stack direction="column" overflow="hidden" gap="2">
                                <Text size="b_xs" color="bodySecondary">
                                    <I18n k={`${TRANSLATION_PREFIX}.freelancer`} />
                                </Text>
                                <Text
                                    size="b_md"
                                    fontWeight="bold"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    whiteSpace="nowrap"
                                >
                                    {sellerName}
                                </Text>
                            </Stack>

                            <Stack flexShrink="0" direction="column" gap="2">
                                <Text size="b_xs" color="bodySecondary">
                                    <I18n k={`${TRANSLATION_PREFIX}.price`} />
                                </Text>
                                <Stack gap="1" alignItems="center">
                                    <Text size="b_md" fontWeight="bold">{`${getCurrencySymbol({
                                        currencyCode: currency,
                                    })}${formatPrice(price)}`}</Text>
                                    {/** TODO: Next phase, comment out when subscription link will be implemented **/}
                                    {/* <Text size="b_sm"> */}
                                    {/*     <Tooltip */}
                                    {/*         content={<I18n k={`${TRANSLATION_PREFIX}.tooltip`} />} */}
                                    {/*         position="bottom" */}
                                    {/*     > */}
                                    {/*         <SubscriptionPageLink */}
                                    {/*             text={translate(`${TRANSLATION_PREFIX}.upgrade_and_save`)} */}
                                    {/*         /> */}
                                    {/*     </Tooltip> */}
                                    {/* </Text> */}
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </Card>

                <LegalTermsDisclaimer paddingTop="1" paddingBottom="4" />
            </Stack>

            <Container position="absolute" left="0px" right="0px" bottom="106px">
                <Divider />
            </Container>

            <Stack
                width={'100%'}
                direction={'row'}
                justifyContent={'spaceBetween'}
                position={'sticky'}
                paddingTop="6"
                alignItems={'center'}
            >
                <Checkbox onChange={() => setSkipNextTime(!skipNextTime)}>
                    <Text size={'b_md'} color={'bodySecondary'}>
                        <I18n k={`${TRANSLATION_PREFIX}.dont_show`} />
                    </Text>
                </Checkbox>
                <Container>
                    <Stack justifyContent="flexEnd">
                        <Button variant="ghost" onClick={close}>
                            <I18n k={`${TRANSLATION_PREFIX}.cancel`} />
                        </Button>
                        <Button onClick={handleSubmit}>
                            <I18n k={`${TRANSLATION_PREFIX}.submit`} />
                        </Button>
                    </Stack>
                </Container>
            </Stack>
        </Stack>
    );
};

const ListEntry = ({ translationKey }: { translationKey: string }) => (
    <Stack alignItems="center">
        <CheckIcon color="grey_1100" />
        <Text size={{ default: 'b_sm', md: 'b_md' }}>
            <I18n k={`${TRANSLATION_PREFIX}.${translationKey}`} />
        </Text>
    </Stack>
);
